import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ShieldQuestion, RefreshCw, Check, Ban, Clock } from 'lucide-react';

import { fetchAdminReviewQueueThunk, decideReviewRequestThunk } from '../../../redux/contentReview/contentReviewActionThunks';

/**
 * Admin side of the GDPR Art 22 / DSA Art 14(1)/17 dispute path: a real
 * human-reviewable queue for moderation appeals and prediction-outcome
 * disputes, covering both compliance findings with one review flow.
 */
const AdminReviewRequestsComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { adminQueue, loading, errors } = useSelector((state) => state.contentReview);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [decisionReasons, setDecisionReasons] = useState({}); // { [reviewId]: text }

  useEffect(() => {
    dispatch(fetchAdminReviewQueueThunk(statusFilter === 'all' ? undefined : statusFilter));
  }, [dispatch, statusFilter]);

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    chipBg: darkMode ? 'bg-gray-700/50' : 'bg-gray-100',
  };

  const handleDecide = (reviewId, decision) => {
    const admin_reason = (decisionReasons[reviewId] || '').trim();
    if (admin_reason.length < 5) return;
    dispatch(decideReviewRequestThunk({ reviewId, decision, admin_reason }));
    setDecisionReasons((prev) => ({ ...prev, [reviewId]: '' }));
  };

  return (
    <div className={`rounded-lg border ${themeClasses.border} ${themeClasses.cardBg} overflow-hidden`}>
      <div className={`p-4 border-b ${themeClasses.border} flex items-center justify-between`}>
        <div>
          <h3 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.text}`}>
            <ShieldQuestion className="w-5 h-5" />
            {t('admin.reviewRequests.title')}
          </h3>
          <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.reviewRequests.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`text-sm rounded-md px-2 py-1.5 ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text}`}
          >
            <option value="pending">{t('admin.reviewRequests.statusLabels.pending')}</option>
            <option value="upheld">{t('admin.reviewRequests.statusLabels.upheld')}</option>
            <option value="overturned">{t('admin.reviewRequests.statusLabels.overturned')}</option>
            <option value="all">{t('admin.reviewRequests.statusLabels.all')}</option>
          </select>
          <button
            onClick={() => dispatch(fetchAdminReviewQueueThunk(statusFilter === 'all' ? undefined : statusFilter))}
            className={`p-2 rounded-md ${themeClasses.chipBg} ${themeClasses.textSecondary} ${themeClasses.hoverBg}`}
          >
            <RefreshCw className={`w-4 h-4 ${loading.adminQueue ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {errors.adminQueue && <div className="p-3 text-sm text-red-400 bg-red-500/10 border-b border-red-500/30">{errors.adminQueue}</div>}

      <div className="divide-y" style={{ borderColor: 'inherit' }}>
        {!loading.adminQueue && adminQueue.length === 0 && (
          <div className={`p-10 text-center text-sm ${themeClasses.textMuted}`}>
            {statusFilter !== 'all'
              ? t('admin.reviewRequests.noRequests', { status: t(`admin.reviewRequests.statusLabels.${statusFilter}`) })
              : t('admin.reviewRequests.noRequestsAny')}
          </div>
        )}
        {adminQueue.map((review) => (
          <div key={review.id} className={`p-5 border-b ${themeClasses.border}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <span className={`text-[11px] font-bold uppercase tracking-wide ${themeClasses.textMuted}`}>
                  {t(`admin.reviewRequests.targetLabels.${review.target_type}`, { defaultValue: review.target_type })}
                </span>
                <h4 className={`text-sm font-semibold ${themeClasses.text}`}>
                  {t('admin.reviewRequests.requestedBy', { username: review.requester_username || t('admin.reviewRequests.requesterFallback', { id: review.user_id }) })}
                </h4>
              </div>
              <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${themeClasses.chipBg} ${themeClasses.textMuted}`}>
                <Clock className="w-3 h-3" />
                {new Date(review.requested_at).toLocaleString()}
              </span>
            </div>

            {review.target_summary && (
              <div className={`text-xs rounded-md p-3 mb-3 ${themeClasses.chipBg} ${themeClasses.textSecondary}`}>
                {review.target_summary.kind === 'prediction' ? (
                  <>
                    <div><b>${review.target_summary.ticker}</b> · {review.target_summary.direction} · {review.target_summary.was_correct ? t('admin.reviewRequests.correct') : t('admin.reviewRequests.incorrect')} · score {review.target_summary.total_score?.toFixed?.(1)}</div>
                    {review.target_summary.excluded_from_scoring && <div className="text-emerald-400 mt-1">{t('admin.reviewRequests.alreadyExcluded')}</div>}
                  </>
                ) : (
                  <>
                    {review.target_summary.title && <div className="font-semibold mb-1">{review.target_summary.title}</div>}
                    {review.target_summary.content_excerpt && <div className="mb-1">{review.target_summary.content_excerpt}</div>}
                    <div className={themeClasses.textMuted}>{t('admin.reviewRequests.moderationReasonLabel')} {review.target_summary.moderation_reason || '—'} · {t('admin.reviewRequests.statusLabel')} {review.target_summary.moderation_status}</div>
                  </>
                )}
              </div>
            )}

            <div className={`text-sm rounded-md p-3 mb-3 border ${themeClasses.border}`}>
              <span className={`text-[11px] font-bold uppercase tracking-wide ${themeClasses.textMuted} block mb-1`}>{t('admin.reviewRequests.usersStatedReason')}</span>
              <p className={themeClasses.text}>{review.reason}</p>
            </div>

            {review.status === 'pending' ? (
              <div className="space-y-2">
                <textarea
                  value={decisionReasons[review.id] || ''}
                  onChange={(e) => setDecisionReasons((prev) => ({ ...prev, [review.id]: e.target.value }))}
                  placeholder={t('admin.reviewRequests.decisionReasonPlaceholder')}
                  rows={2}
                  className={`w-full text-sm rounded-md px-3 py-2 ${themeClasses.inputBg} border ${themeClasses.inputBorder} ${themeClasses.text} resize-none`}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDecide(review.id, 'overturned')}
                    disabled={(decisionReasons[review.id] || '').trim().length < 5 || loading.decide}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {t('admin.reviewRequests.overturnDecision')}
                  </button>
                  <button
                    onClick={() => handleDecide(review.id, 'upheld')}
                    disabled={(decisionReasons[review.id] || '').trim().length < 5 || loading.decide}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${themeClasses.chipBg} ${themeClasses.textSecondary} ${themeClasses.hoverBg} disabled:opacity-50`}
                  >
                    <Ban className="w-3.5 h-3.5" />
                    {t('admin.reviewRequests.upholdDecision')}
                  </button>
                </div>
              </div>
            ) : (
              <div className={`text-xs px-3 py-2 rounded-md ${review.status === 'overturned' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-500/10 text-slate-400'}`}>
                <b className="capitalize">{t(`admin.reviewRequests.statusLabels.${review.status}`, { defaultValue: review.status })}</b> -- {review.admin_decision_reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminReviewRequestsComponent;
